import { format, formatRelative, isPast } from 'date-fns';
import { de } from 'date-fns/esm/locale';
import { DATE_FORMATS, TIME_FORMATS } from '@src/constants/dateFormat';

/**
 * Robust timezone-safe date parsing function that handles multiple date formats
 * without timezone conversion issues. This prevents dates from shifting by one day
 * due to browser timezone calculations.
 *
 * @param dateInput - Date input in various formats (string, Date, null, undefined)
 * @returns Date object representing the exact local date without timezone adjustment, or null if invalid
 */
const parseLocalDateSafe = (
  dateInput: string | Date | null | undefined
): Date | null => {
  if (!dateInput) return null;

  // If already a Date object, ensure we work with local date only
  if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) return null;
    // Create a new date using only the date components to avoid timezone issues
    return new Date(
      dateInput.getFullYear(),
      dateInput.getMonth(),
      dateInput.getDate()
    );
  }

  // Handle string inputs
  if (typeof dateInput === 'string') {
    const cleanDateString = dateInput.trim();

    // Handle ISO date format (YYYY-MM-DD) - most common case
    const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const isoMatch = cleanDateString.match(isoDateRegex);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const parsedYear = parseInt(year, 10);
      const parsedMonth = parseInt(month, 10) - 1; // Month is 0-indexed
      const parsedDay = parseInt(day, 10);

      // Validate the parsed values
      if (
        parsedYear >= 1900 &&
        parsedYear <= 2100 &&
        parsedMonth >= 0 &&
        parsedMonth <= 11 &&
        parsedDay >= 1 &&
        parsedDay <= 31
      ) {
        return new Date(parsedYear, parsedMonth, parsedDay);
      }
    }

    // Handle ISO datetime format (YYYY-MM-DDTHH:mm:ss...) - extract date part
    const isoDatetimeRegex = /^(\d{4})-(\d{2})-(\d{2})T/;
    const isoDatetimeMatch = cleanDateString.match(isoDatetimeRegex);
    if (isoDatetimeMatch) {
      const [, year, month, day] = isoDatetimeMatch;
      const parsedYear = parseInt(year, 10);
      const parsedMonth = parseInt(month, 10) - 1;
      const parsedDay = parseInt(day, 10);

      if (
        parsedYear >= 1900 &&
        parsedYear <= 2100 &&
        parsedMonth >= 0 &&
        parsedMonth <= 11 &&
        parsedDay >= 1 &&
        parsedDay <= 31
      ) {
        return new Date(parsedYear, parsedMonth, parsedDay);
      }
    }

    // Handle MM/DD/YYYY format
    const usDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const usDateMatch = cleanDateString.match(usDateRegex);
    if (usDateMatch) {
      const [, month, day, year] = usDateMatch;
      const parsedYear = parseInt(year, 10);
      const parsedMonth = parseInt(month, 10) - 1;
      const parsedDay = parseInt(day, 10);

      if (
        parsedYear >= 1900 &&
        parsedYear <= 2100 &&
        parsedMonth >= 0 &&
        parsedMonth <= 11 &&
        parsedDay >= 1 &&
        parsedDay <= 31
      ) {
        return new Date(parsedYear, parsedMonth, parsedDay);
      }
    }

    // Handle DD/MM/YYYY format
    const euDateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const euDateMatch = cleanDateString.match(euDateRegex);
    if (euDateMatch) {
      const [, day, month, year] = euDateMatch;
      const parsedYear = parseInt(year, 10);
      const parsedMonth = parseInt(month, 10) - 1;
      const parsedDay = parseInt(day, 10);

      // Only use this format if day > 12 to avoid ambiguity with MM/DD/YYYY
      if (
        parsedDay > 12 &&
        parsedYear >= 1900 &&
        parsedYear <= 2100 &&
        parsedMonth >= 0 &&
        parsedMonth <= 11 &&
        parsedDay >= 1 &&
        parsedDay <= 31
      ) {
        return new Date(parsedYear, parsedMonth, parsedDay);
      }
    }

    // Handle YYYY/MM/DD format
    const isoSlashRegex = /^(\d{4})\/(\d{2})\/(\d{2})$/;
    const isoSlashMatch = cleanDateString.match(isoSlashRegex);
    if (isoSlashMatch) {
      const [, year, month, day] = isoSlashMatch;
      const parsedYear = parseInt(year, 10);
      const parsedMonth = parseInt(month, 10) - 1;
      const parsedDay = parseInt(day, 10);

      if (
        parsedYear >= 1900 &&
        parsedYear <= 2100 &&
        parsedMonth >= 0 &&
        parsedMonth <= 11 &&
        parsedDay >= 1 &&
        parsedDay <= 31
      ) {
        return new Date(parsedYear, parsedMonth, parsedDay);
      }
    }

    // Last resort: try native Date parsing but extract only date components
    try {
      const nativeDate = new Date(cleanDateString);
      if (!isNaN(nativeDate.getTime())) {
        // Extract only the date components to avoid timezone issues
        return new Date(
          nativeDate.getFullYear(),
          nativeDate.getMonth(),
          nativeDate.getDate()
        );
      }
    } catch (error) {
      console.warn('Failed to parse date string:', cleanDateString, error);
    }
  }

  return null;
};

/**
 *  For format Dates
 */
const formatDate = (date: string | Date, dateFormat: string): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if the resulting date is valid
  if (isNaN(dateObj.getTime())) return '';

  return format(dateObj, dateFormat);
};

/**
 *  For format Times
 */
const formatTime = (timeString: string): string => {
  const date = new Date(timeString);
  return format(date, TIME_FORMATS.H_MM_A);
};

const formatDateRelative = (date: string): string => {
  const formatRelativeLocale = {
    lastWeek: DATE_FORMATS.MM_DD_YYYY,
    yesterday: "'Yesterday'",
    today: "'Today'",
    tomorrow: "'Tomorrow'",
    nextWeek: DATE_FORMATS.MM_DD_YYYY,
    other: DATE_FORMATS.MM_DD_YYYY,
  };

  const locale = {
    ...de,
    formatRelative: (token: string) =>
      formatRelativeLocale[token as keyof typeof formatRelativeLocale],
  };

  return formatRelative(new Date(date), new Date(), { locale });
};

/**
 * For the Date with Time
 * @param {string} date
 **/
const formatDateAndTime = (date: string) => {
  const isDate = formatDateRelative(date);
  const isTime = formatTime(date);
  return `${isDate} at ${isTime} `;
};

/**
 * Checks if the given date is expired.
 *
 * @param {string} date - The date to check in ISO string format.
 * @return {boolean} Returns true if the date is in the past, false otherwise.
 */
const isPastDate = (date: string) => {
  if (!date) return false;

  const expiryDate = new Date(date);

  const [year, month, day, hours, minutes, seconds] = [
    expiryDate.getFullYear(),
    expiryDate.getMonth(),
    expiryDate.getDate(),
    expiryDate.getHours(),
    expiryDate.getMinutes(),
    expiryDate.getSeconds(),
  ];
  const dateToCompare = new Date(year, month, day, hours, minutes, seconds);

  if (isPast(dateToCompare)) {
    return true;
  }

  return false;
};

/**
 * Validates if a given date string is in MM/DD/YYYY format and represents a valid date.
 *
 * @param {string} date - The date string to be validated.
 * @return {boolean} Returns true if the date string is in the correct format and is a valid date, false otherwise.
 */
function isValidDate(date: string) {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (!regex.test(date)) {
    return false;
  }

  const [month, day, year] = date.split('/').map(Number);

  const currentYear = new Date().getFullYear();
  if (year < 1800 || year > currentYear + 400) {
    return false;
  }

  const daysInMonth = new Date(year, month, 0).getDate();

  if (day <= 0 || day > daysInMonth) {
    return false;
  }

  const dateObj = new Date(year, month - 1, day);
  return (
    dateObj.getFullYear() === year &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getDate() === day
  );
}

/**
 * Removes all non-numeric characters from the input, inserts slashes at specific positions, and limits the input length to 10 characters.
 *
 * @param {string} input - The input string to be processed.
 * @return {string} The processed input string with non-numeric characters removed, slashes inserted, and length limited to 10 characters.
 */
function maskDateInput(input: string) {
  // Remove all non-numeric characters
  input = input.replace(/\D/g, '');

  // Insert slashes at the correct positions
  if (input.length > 2) {
    input = input.slice(0, 2) + '/' + input.slice(2);
  }
  if (input.length > 5) {
    input = input.slice(0, 5) + '/' + input.slice(5);
  }

  // Limit the input length to 10 characters
  if (input.length > 10) {
    input = input.slice(0, 10);
  }

  return input;
}

// Export all functions
export {
  parseLocalDateSafe,
  formatDate,
  formatTime,
  formatDateRelative,
  formatDateAndTime,
  isPastDate,
  isValidDate,
  maskDateInput,
};
